import React, { useState } from "react";
import { ChevronDown, ExternalLink } from "lucide-react";
import { duelbits, CODE } from "../mock";

/*
 * Bonuses — single horizontal, transparent Duelbits card.
 */
const BonusesSection = () => {
  const [open, setOpen] = useState(false);

  return (
    <section id="bonuses" className="relative py-24 bg-[#171b26]" data-testid="bonuses-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <h2 className="font-display font-extrabold italic text-4xl sm:text-6xl uppercase tracking-tight">
            <span className="text-white">Bon</span>
            <span className="text-[#3ddc84]">us</span>
          </h2>
          <p className="mt-3 uppercase text-xs font-bold tracking-[0.25em] text-[#7e879c]">
            Claim instantly & enjoy
          </p>
        </div>

        {/* horizontal transparent Duelbits card */}
        <div
          className="duelbits-card group relative rounded-2xl overflow-hidden"
          data-testid="bonus-card-duelbits"
        >
          {/* soft green sheen */}
          <div className="absolute inset-0 pointer-events-none opacity-60">
            <div className="absolute -top-24 -left-16 w-[380px] h-[280px] rounded-full blur-[100px] bg-[#3ddc84]/15" />
            <div className="absolute -bottom-28 -right-10 w-[320px] h-[260px] rounded-full blur-[90px] bg-[#3ddc84]/10" />
          </div>

          <div className="relative flex flex-col lg:flex-row items-center gap-8 lg:gap-10 px-7 sm:px-10 py-9">
            {/* logo */}
            <div className="shrink-0 flex items-center justify-center">
              <img
                src={duelbits.logo}
                alt="Duelbits"
                className="h-12 sm:h-14 w-auto object-contain drop-shadow-[0_6px_18px_rgba(0,0,0,0.4)] group-hover:scale-105 transition-transform duration-300"
                data-testid="duelbits-logo"
              />
            </div>

            {/* divider */}
            <div className="hidden lg:block w-px self-stretch bg-gradient-to-b from-transparent via-[#3a4258] to-transparent" />
            <div className="lg:hidden h-px w-2/3 bg-gradient-to-r from-transparent via-[#3a4258] to-transparent" />

            {/* bonus info */}
            <div className="flex-1 text-center lg:text-left">
              <p className="uppercase text-[10px] font-bold tracking-[0.22em] text-[#7e879c]">
                Main Bonus
              </p>
              <p className="mt-1.5 font-display font-extrabold italic text-2xl sm:text-3xl text-white leading-tight uppercase">
                {duelbits.mainBonus}
              </p>
              <p className="mt-3 text-[13px] font-semibold text-[#3ddc84] uppercase tracking-wide">
                {duelbits.extra}
              </p>
            </div>

            {/* CTA */}
            <div className="shrink-0 flex flex-col items-center gap-3">
              <a
                href={duelbits.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-duel inline-flex items-center justify-center gap-2 px-9 py-4 text-sm"
                data-testid="claim-bonus-duelbits"
              >
                Claim Bonus
                <ExternalLink size={15} strokeWidth={2.5} />
              </a>
              <button
                onClick={() => setOpen(!open)}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#97a1b8] hover:text-white transition-colors duration-200"
                data-testid="how-to-claim-duelbits"
              >
                How to claim bonus
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                />
              </button>
            </div>
          </div>

          {/* how to claim — expands under the whole card */}
          <div
            className="relative overflow-hidden transition-all duration-300"
            style={{ maxHeight: open ? 220 : 0, opacity: open ? 1 : 0 }}
          >
            <div className="px-7 sm:px-10 pb-8 pt-1">
              <div className="h-px w-full bg-[#2c3448] mb-5" />
              <ol className="grid sm:grid-cols-3 gap-4 text-left text-[13px] text-[#aeb7cc]">
                {duelbits.steps.map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-[#3ddc84]/15 text-[#3ddc84] text-[11px] font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ol>
              <p className="mt-5 text-[11px] uppercase tracking-[0.2em] font-bold text-[#7e879c]">
                Use code <span className="text-[#3ddc84]">“{CODE}”</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BonusesSection;
